import React from 'react';
import * as Relay from 'react-relay';
import { fromGlobalId } from 'graphql-relay';
import { defineMessages, injectIntl } from 'react-intl';
import { withRouter } from 'react-router';
import urlMapping from '../app/URLMapping';
import FlowTestResultView from './FlowTestResultView';
import type { EditorTarget } from './FlowTestResultView';
import AcceptSnapshotMutation from './AcceptSnapshotMutation';
import SetExpectedOutputMutation from './SetExpectedOutputMutation';
import DeleteTestInvocationMutation from './DeleteTestInvocationMutation';
import UpdateEnvironmentStateMutation from './UpdateEnvironmentStateMutation';
import CreateDerivedTestMutation from './CreateDerivedTestMutation';
import { notification } from './notification';
import { getReviewMutationOutcome, ReviewMutationOutcome } from './mutationPayload';
import {
  buildDisplayComputedDiffPayload,
  parseComputedDiffPayload,
  parseSnapshotOutputPayload,
  cleanSnapshotOutputForDisplay,
  stripPrivateSnapshotFields,
} from '../../common/snapshot-testing/computedDiffPayload';
import type { ComputedDiffWrite, ComputedDiffGroup, ComputedDiffPayload } from '../../common/snapshot-testing/computedDiffPayload';
import type { ReviewTestInvocation as TestInvocation } from './testcaseWireTypes';

const uuidRegex = /[0-9a-fA-F\-]{30,40}/;

const messages = defineMessages({
  deriveFailed: {
    id: 'snapshotTesting.deriveFailed',
    defaultMessage: 'Could not confirm creation. Check the testcase list before retrying to avoid duplicates.',
  },
  conflictDetected: {
    id: 'snapshotTesting.conflictDetected',
    defaultMessage: 'Conflict detected. Please refresh and try again.',
  },
  acceptFailed: {
    id: 'snapshotTesting.acceptFailed',
    defaultMessage: 'Failed to accept snapshot. Please try again.',
  },
  setExpectedOutputFailed: {
    id: 'snapshotTesting.setExpectedOutputFailed',
    defaultMessage: 'Failed to set expected output. Please try again.',
  },
  updateSnapshotFailed: {
    id: 'snapshotTesting.updateSnapshotFailed',
    defaultMessage: 'Failed to update snapshot. Please try again.',
  },
  deleteFailed: {
    id: 'snapshotTesting.deleteFailed',
    defaultMessage: 'Failed to delete test invocation. Please try again.',
  },
  confirmDelete: {
    id: 'snapshotTesting.confirmDelete',
    defaultMessage: 'Are you sure you want to delete this test invocation?',
  },
});

// Define missing types locally
interface InjectedIntl {
  formatMessage: (messageDescriptor: any, values?: any) => string;
}

interface InjectedIntlProps {
  intl: InjectedIntl;
}

interface InjectedRouter {
  push: (path: string) => void;
  replace: (path: string) => void;
  go: (n: number) => void;
  goBack: () => void;
  goForward: () => void;
  setRouteLeaveHook: (route: any, hook: any) => Function;
  isActive: (path: string, indexOnly?: boolean) => boolean;
}

interface WithRouterProps {
  router: InjectedRouter;
  params: any;
  location: any;
  routes: any;
}

export { ComputedDiffWrite, ComputedDiffGroup, ComputedDiffPayload, parseComputedDiffPayload, parseSnapshotOutputPayload, cleanSnapshotOutputForDisplay, buildDisplayComputedDiffPayload } from '../../common/snapshot-testing/computedDiffPayload';

export function cleanWriteForDisplay(write: any): any {
  if (!write || typeof write !== 'object') {
    return write;
  }

  const {
    stepId, stepInfo, status, _writeKey, _writeKeyBase, headers, requestHeaders, responseHeaders,
    requestHash, requestHashSource,
    ...rest
  } = write;
  return stripPrivateSnapshotFields(rest);
}

interface SnapshotValidationContainerProps extends InjectedIntlProps, WithRouterProps {
  testInvocation: TestInvocation;
  params: {
    organisationId: string;
    flowId: string;
    id?: string;
    runId?: string;
  };
  relay: any;
}

interface SnapshotValidationContainerState {
  expandedGroupIndices: Set<number>;
  diffsCommand: { action: string; id: number } | null;
  conflictError: string | null;
}

export function normalizeSnapshotEntityId(id?: string | null): string | null {
  if (!id) {
    return null;
  }

  if (uuidRegex.test(id)) {
    return id;
  }

  try {
    const globalId = fromGlobalId(id);
    return globalId.id || id;
  } catch (e) {
    return id;
  }
}

export function getPreferredSnapshotEntityId(entity?: { id?: string | null; realId?: string | null } | null): string | null {
  if (!entity) {
    return null;
  }

  return entity.realId || normalizeSnapshotEntityId(entity.id);
}

export function isSameSnapshotEntityId(entity?: { id?: string | null; realId?: string | null } | null, otherId?: string | null): boolean {
  const normalizedEntityId = getPreferredSnapshotEntityId(entity);
  const normalizedOtherId = normalizeSnapshotEntityId(otherId);

  return !!normalizedEntityId && normalizedEntityId === normalizedOtherId;
}

export class SnapshotValidationContainer extends React.Component<SnapshotValidationContainerProps, SnapshotValidationContainerState> {
  constructor(props: SnapshotValidationContainerProps) {
    super(props);
    this.state = {
      expandedGroupIndices: new Set(),
      diffsCommand: null,
      conflictError: null,
    };
  }

  private unmounted = false;
  private reviewRequestGeneration = 0;

  componentWillUnmount() {
    this.unmounted = true;
  }

  componentWillReceiveProps(nextProps: SnapshotValidationContainerProps) {
    // A different testcase or run must not inherit the previous page's review feedback.
    if (this.getReviewOwnerKey(nextProps) !== this.getReviewOwnerKey()) {
      this.setState({ conflictError: null, expandedGroupIndices: new Set(), diffsCommand: null });
    }
  }

  /** Identity of the page owner a review action was started for. */
  getReviewOwnerKey(props: SnapshotValidationContainerProps = this.props): string {
    return `${getPreferredSnapshotEntityId(props.testInvocation) || ''}|${normalizeSnapshotEntityId(props.params.runId) || ''}`;
  }

  /**
   * View effects (state, refresh, navigation) of a mutation callback only apply while the page
   * still shows the owner that started it. A disposed or retargeted page ignores them; the
   * server-side result of the mutation is not undone by this.
   */
  isReviewOwnerCurrent(ownerKey: string): boolean {
    return !this.unmounted && this.getReviewOwnerKey() === ownerKey;
  }

  getSelectedRun() {
    const { testInvocation, params } = this.props;
    const runs = testInvocation?.runs?.edges ? testInvocation.runs.edges.map(e => e?.node).filter(Boolean) : [];
    const selectedRuns = testInvocation?.selectedRun?.edges ? testInvocation.selectedRun.edges.map(e => e?.node).filter(Boolean) : [];

    if (params.runId) {
      // An explicit run link resolves only to that exact run. Never substitute another
      // (e.g. the latest) run: review actions would otherwise target a run the reviewer
      // did not ask for.
      return selectedRuns.find(r => r && isSameSnapshotEntityId(r, params.runId))
        || runs.find(r => r && isSameSnapshotEntityId(r, params.runId))
        || null;
    }

    return selectedRuns[0] || runs[0] || null;
  }

  isRequestedRunUnavailable() {
    return !!this.props.params.runId && !this.getSelectedRun();
  }

  isSelectedRunHistorical() {
    const { testInvocation, params } = this.props;
    const runs = testInvocation?.runs?.edges ? testInvocation.runs.edges.map(e => e?.node).filter(Boolean) : [];
    const selected = this.getSelectedRun();
    return !!selected && (runs.length > 0
      ? !isSameSnapshotEntityId(selected, getPreferredSnapshotEntityId(runs[0]))
      : !!params.runId);
  }

  isCurrentLatestRunTarget(target?: EditorTarget | null) {
    const runs = this.props.testInvocation?.runs?.edges ? this.props.testInvocation.runs.edges.map(e => e?.node).filter(Boolean) : [];
    const latestRunId = runs.length > 0 ? getPreferredSnapshotEntityId(runs[0]) : null;
    return !!target && !!target.testRunId && !!latestRunId && isSameSnapshotEntityId({ id: target.testRunId }, latestRunId);
  }

  refreshViaCanonicalRoute = () => {
    const { organisationId, flowId, runId } = this.props.params;
    const invocationId = getPreferredSnapshotEntityId(this.props.testInvocation) || this.props.params.id;

    const url = runId
      ? urlMapping.getUrl('snapshotTesting', 'validateRun', {
        organisation_id: organisationId,
        flow_id: flowId,
        id: invocationId,
        runId: normalizeSnapshotEntityId(runId),
      })
      : urlMapping.getUrl('snapshotTesting', 'validate', {
        organisation_id: organisationId,
        flow_id: flowId,
        id: invocationId,
      });

    // The canonical URL only normalizes identity. With a warm Relay cache the route replacement
    // alone re-renders cached data, so explicitly refetch the review owner as well. Open editor
    // drafts stay pinned to their own revision (see FlowTestResultView editor sessions).
    this.props.router.replace(url);
    if (this.props.relay && typeof this.props.relay.forceFetch === 'function') {
      this.props.relay.forceFetch();
    }
  };

  /**
   * Commits a review mutation and settles with its acknowledged outcome. Only an explicit
   * `success: true` payload counts as success; conflicts refresh the review state.
   */
  commitReviewMutation(mutation: any, payloadKey: string, failureMessage: any): Promise<ReviewMutationOutcome> {
    const { intl } = this.props;
    const ownerKey = this.getReviewOwnerKey();
    const generation = ++this.reviewRequestGeneration;
    // Only the newest request of the current owner may update the page.
    const mayApplyViewEffects = () => this.isReviewOwnerCurrent(ownerKey) && generation === this.reviewRequestGeneration;
    this.setState({ conflictError: null });

    return new Promise<ReviewMutationOutcome>((resolve) => {
      Relay.Store.commitUpdate(mutation, {
        onSuccess: (response: any) => {
          const outcome = getReviewMutationOutcome(
            response && response[payloadKey],
            intl.formatMessage(failureMessage),
            intl.formatMessage(messages.conflictDetected)
          );
          if (mayApplyViewEffects()) {
            if (!outcome.ok) {
              this.setState({ conflictError: outcome.error });
            }
            if (outcome.ok || outcome.conflict) {
              this.refreshViaCanonicalRoute();
            }
          }
          resolve(outcome);
        },
        onFailure: () => {
          const error = intl.formatMessage(failureMessage);
          if (mayApplyViewEffects()) {
            this.setState({ conflictError: error });
          }
          resolve({ ok: false, conflict: false, error });
        },
      });
    });
  }

  refusedOutcome(failureMessage: any): Promise<ReviewMutationOutcome> {
    return Promise.resolve({ ok: false, conflict: false, error: this.props.intl.formatMessage(failureMessage) } as ReviewMutationOutcome);
  }

  handleDerive = (name: string, reason: string, source: EditorTarget): Promise<ReviewMutationOutcome> => {
    if (this.isRequestedRunUnavailable() || this.isSelectedRunHistorical()
      || !isSameSnapshotEntityId(this.props.testInvocation, source.realId || source.id)) {
      return this.refusedOutcome(messages.deriveFailed);
    }
    const ownerKey = this.getReviewOwnerKey();
    const generation = ++this.reviewRequestGeneration;
    const mayApply = () => this.isReviewOwnerCurrent(ownerKey) && generation === this.reviewRequestGeneration;
    const { organisationId, flowId } = this.props.params;
    return new Promise((resolve) => {
      Relay.Store.commitUpdate(new CreateDerivedTestMutation({ source, name, reason }), {
        onSuccess: (response: any) => {
          const payload = response && response.createDerivedTestInvocation;
          let outcome = getReviewMutationOutcome(payload, this.props.intl.formatMessage(messages.deriveFailed), this.props.intl.formatMessage(messages.conflictDetected));
          const child = payload && payload.testInvocation;
          const childId = getPreferredSnapshotEntityId(child);
          if (outcome.ok && (!childId || isSameSnapshotEntityId(source, childId))) {
            outcome = { ok: false, conflict: false, error: this.props.intl.formatMessage(messages.deriveFailed) };
          }
          if (mayApply()) {
            if (outcome.ok) {
              this.props.router.push(urlMapping.getUrl('snapshotTesting', 'validate', {
                organisation_id: child.organisation_id || organisationId,
                flow_id: child.flow_id || flowId,
                id: childId,
              }));
            } else if (outcome.conflict) {
              this.refreshViaCanonicalRoute();
            }
          }
          resolve(outcome);
        },
        onFailure: () => resolve({ ok: false, conflict: false, error: this.props.intl.formatMessage(messages.deriveFailed) }),
      });
    });
  };

  handleAccept = (): Promise<ReviewMutationOutcome> => {
    const lastRun = this.getSelectedRun();

    if (!lastRun || this.isRequestedRunUnavailable() || this.isSelectedRunHistorical()) {
      return this.refusedOutcome(messages.acceptFailed);
    }

    return this.commitReviewMutation(
      new AcceptSnapshotMutation({
        testInvocation: this.props.testInvocation,
        testRunId: getPreferredSnapshotEntityId(lastRun) || undefined,
      }),
      'acceptTestInvocationSnapshot',
      messages.acceptFailed
    );
  };

  /**
   * Resolves the testcase an editor save applies to. An editor session is pinned to the testcase
   * identity and version it was opened against; a save is never retargeted to newer props.
   */
  getEditorSaveTarget(target?: EditorTarget): EditorTarget | null {
    const { testInvocation } = this.props;
    if (!target) {
      return { id: testInvocation.id, realId: testInvocation.realId, version: testInvocation.version, testRunId: getPreferredSnapshotEntityId(this.getSelectedRun()) || null };
    }
    if (!isSameSnapshotEntityId(testInvocation, target.realId || target.id)) {
      return null;
    }
    return target;
  }

  handleManualUpdate = (expectedOutput: any, target?: EditorTarget): Promise<ReviewMutationOutcome> => {
    const saveTarget = this.getEditorSaveTarget(target);
    if (this.isRequestedRunUnavailable() || (this.isSelectedRunHistorical() && !this.isCurrentLatestRunTarget(saveTarget)) || !saveTarget) {
      return this.refusedOutcome(messages.setExpectedOutputFailed);
    }

    return this.commitReviewMutation(
      new SetExpectedOutputMutation({
        testInvocation: saveTarget,
        expectedOutput: expectedOutput,
        testRunId: saveTarget.testRunId || null,
      }),
      'setExpectedOutput',
      messages.setExpectedOutputFailed
    );
  };

  handleEditEnvironmentState = (environmentStateJson: string, target?: EditorTarget): Promise<ReviewMutationOutcome> => {
    const saveTarget = this.getEditorSaveTarget(target);
    if (this.isRequestedRunUnavailable() || (this.isSelectedRunHistorical() && !this.isCurrentLatestRunTarget(saveTarget)) || !saveTarget) {
      return this.refusedOutcome(messages.updateSnapshotFailed);
    }

    return this.commitReviewMutation(
      new UpdateEnvironmentStateMutation({
        testInvocation: saveTarget,
        environmentState: environmentStateJson,
        testRunId: saveTarget.testRunId || null,
      }),
      'updateTestInvocationEnvironmentState',
      messages.updateSnapshotFailed
    );
  };

  handleDelete = () => {
    if (!confirm(this.props.intl.formatMessage(messages.confirmDelete))) {
      return;
    }

    const ownerKey = this.getReviewOwnerKey();
    const { organisationId, flowId } = this.props.params;

    Relay.Store.commitUpdate(
      new DeleteTestInvocationMutation({
        testInvocation: this.props.testInvocation,
      }),
      {
        onSuccess: (response: any) => {
          const deleteFailed = this.props.intl.formatMessage(messages.deleteFailed);
          const outcome = getReviewMutationOutcome(response && response.deleteTestInvocation, deleteFailed, deleteFailed);
          if (!outcome.ok) {
            notification.error(outcome.error);
            return;
          }
          if (!this.isReviewOwnerCurrent(ownerKey)) {
            // The deletion is committed, but the page has moved on; do not navigate it.
            return;
          }
          const url = urlMapping.getUrl('snapshotManagement', 'manage', {
            organisation_id: organisationId,
            id: flowId
          });
          this.props.router.push(url);
        },
        onFailure: (error: any) => {
          console.error('Failed to delete test invocation', error);
          notification.error(this.props.intl.formatMessage(messages.deleteFailed));
        },
      }
    );
  };

  handleGroupToggle = (index: number) => {
    const newExpanded = new Set(this.state.expandedGroupIndices);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    this.setState({ expandedGroupIndices: newExpanded });
  };

  handleExpandAllGroups = (count: number) => {
    const all = new Set<number>();
    for (let i = 0; i < count; i++) all.add(i);
    this.setState({ expandedGroupIndices: all });
  };

  handleCollapseAllGroups = () => {
    this.setState({ expandedGroupIndices: new Set() });
  };

  handleExpandAllDiffs = () => {
    this.setState({ diffsCommand: { action: 'expand', id: Date.now() } });
  };

  handleCollapseAllDiffs = () => {
    this.setState({ diffsCommand: { action: 'collapse', id: Date.now() } });
  };

  render() {
    const { testInvocation, params } = this.props;

    if (!testInvocation) return null;

    const runs = testInvocation?.runs?.edges ? testInvocation.runs.edges.map(e => e?.node).filter(Boolean) : [];
    const lastRun = this.getSelectedRun();

    const computedDiffPayload = buildDisplayComputedDiffPayload(testInvocation, lastRun);
    const isDiffUnavailable = !!lastRun && !computedDiffPayload;
    const groups = computedDiffPayload?.groups || [];
    const expectedSummary = computedDiffPayload && Object.prototype.hasOwnProperty.call(computedDiffPayload, 'expectedSummary')
      ? computedDiffPayload.expectedSummary
      : null;
    const actualSummary = computedDiffPayload && Object.prototype.hasOwnProperty.call(computedDiffPayload, 'actualSummary')
      ? computedDiffPayload.actualSummary
      : null;
    const hasSummaryMismatch = !!computedDiffPayload?.hasSummaryMismatch;
    const mismatchCount = computedDiffPayload?.mismatchCount || 0;
    const maxWritesLen = computedDiffPayload?.maxWritesLen || 0;

    const isRunUnavailable = this.isRequestedRunUnavailable();
    // An explicitly linked run is only "current" when the recent-history head proves it is the latest run.
    const isHistoricalRun = this.isSelectedRunHistorical();

    return (
      <FlowTestResultView
        actualSummary={actualSummary}
        conflictError={this.state.conflictError}
        diffsCommand={this.state.diffsCommand}
        expandedGroupIndices={this.state.expandedGroupIndices}
        expectedSummary={expectedSummary}
        groups={groups}
        hasSummaryMismatch={hasSummaryMismatch}
        isDiffUnavailable={isDiffUnavailable}
        intl={this.props.intl}
        isHistoricalRun={isHistoricalRun}
        isRunUnavailable={isRunUnavailable}
        lastRun={lastRun}
        latestRunId={runs.length > 0 ? getPreferredSnapshotEntityId(runs[0]) : null}
        maxWritesLen={maxWritesLen}
        mismatchCount={mismatchCount}
        onDerive={this.handleDerive}
        onAccept={this.handleAccept}
        onCollapseAllDiffs={this.handleCollapseAllDiffs}
        onCollapseAllGroups={this.handleCollapseAllGroups}
        onDelete={this.handleDelete}
        onExpandAllDiffs={this.handleExpandAllDiffs}
        onExpandAllGroups={() => this.handleExpandAllGroups(groups.length)}
        onGroupToggle={this.handleGroupToggle}
        onManualUpdate={this.handleManualUpdate}
        onEditEnvironmentState={this.handleEditEnvironmentState}
        params={params}
        testInvocation={testInvocation}
      />
    );
  }
}

export default Relay.createContainer(injectIntl(withRouter(SnapshotValidationContainer)), {
  initialVariables: {
    id: null,
    runId: null,
  },
  fragments: {
    testInvocation: () => Relay.QL`
      fragment on TestInvocation {
        id
        realId
        name
        organisation_id
        flow_id
        flowDef_id
        flowDefVersion_id
        sourceFlowInvocation_id
        expectedOutput
        version
        createdAt
        updatedAt
        lastStatus
        lastRunAt
        lastDuration
        lastError
        environmentState
        fetchIds
        startType
        originalRequest
        originalHeaders
        originalUrl
        originalRequestUrl
        runs(first: 100) {
          edges {
            node {
              id
              realId
              status
              createdAt
              error
              duration
              expiresIn
            }
          }
        }
        selectedRun: runs(first: 1, id: $runId) {
          edges {
            node {
              id
              realId
              status
              createdAt
              actualOutput
              computedDiff
              error
              duration
              expiresIn
            }
          }
        }
      }
    `,
  },
});
